<template>
    <DxScrollView class="campaign-details-container">
        <div v-if="campaign">
            <!-- Back Button -->
            <div class="back-button-section">
                <DxButton icon="arrowleft" text="Înapoi la listă" styling-mode="text" @click="$emit('back')"
                    class="back-btn" />
            </div>

            <!-- Header Section -->
            <div class="campaign-header">
                <div class="header-top">
                    <div class="header-left">
                        <div class="campaign-icon">
                            <i class="dx-icon-box"></i>
                        </div>
                        <div class="header-info">
                            <h1 class="campaign-title">{{ campaign.Denumire || 'Campanie fără titlu' }}</h1>
                            <div class="campaign-meta">
                                <span class="meta-item">
                                    <i class="dx-icon-event"></i>
                                    {{ formatDate(campaign.DataStart) }} - {{ formatDate(campaign.DataEnd) }}
                                </span>
                                <span class="meta-separator">•</span>
                                <span class="meta-item">
                                    <i class="dx-icon-money"></i>
                                    {{ formatCurrency(campaign.Buget) }}
                                </span>
                                <span class="meta-separator">•</span>
                                <span class="meta-item">
                                    <i class="dx-icon-home"></i>
                                    {{ campaign.Industrie?.Nume || campaign.IndustrieNume || 'Fără industrie' }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="header-actions">
                        <span :class="['status-badge', campaign.CurentActiv ? 'status-active' : 'status-inactive']">
                            <span class="status-dot"></span>
                            {{ campaign.CurentActiv ? 'Activ' : 'Inactiv' }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Two Column Layout -->
            <div class="two-column-layout">
                <!-- Left Column - Details Form -->
                <div class="left-column">
                    <div class="section-card">
                        <div class="card-header">
                            <div class="card-header-icon">
                                <i class="dx-icon-doc"></i>
                            </div>
                            <h3 class="card-header-title">Detalii Campanie</h3>
                        </div>
                        <div class="card-body">
                            <CampaniiForm :campaign-data="campaign" />
                        </div>
                    </div>
                </div>

                <!-- Right Column - Contacts -->
                <div class="right-column">
                    <div class="section-card">
                        <div class="card-header">
                            <div class="card-header-icon"
                                style="background: linear-gradient(135deg, #10b981 0%, #34d399 100%);">
                                <i class="dx-icon-group"></i>
                            </div>
                            <h3 class="card-header-title">Contacte Asociate</h3>
                        </div>
                        <div class="card-body">
                            <ContactCampanii :campaign-id="campaign.ID" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="no-data">
            <div class="no-data-icon">
                <i class="dx-icon-box"></i>
            </div>
            <p class="no-data-text">Nicio campanie selectată</p>
        </div>
    </DxScrollView>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DxScrollView } from 'devextreme-vue/scroll-view';
import { DxButton } from 'devextreme-vue/button';
import CampaniiForm from '@/components/library/campanii-form.vue';
import ContactCampanii from '@/components/library/contact-campanii.vue';

export default defineComponent({
    name: 'GridCampaniiDetalii',
    components: {
        DxScrollView,
        DxButton,
        CampaniiForm,
        ContactCampanii
    },
    props: {
        campaign: Object,
    },
    computed: {
        daysRemaining(): number {
            if (!this.campaign?.DataEnd) return 0;
            const end = new Date(this.campaign.DataEnd).getTime();
            const now = new Date().getTime();
            const diff = end - now;
            return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        }
    },
    methods: {
        formatCurrency(value: number): string {
            if (!value && value !== 0) return '-';
            return new Intl.NumberFormat('ro-RO', {
                style: 'currency',
                currency: 'RON'
            }).format(value);
        },
        formatDate(dateString: string): string {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('ro-RO', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(date);
        }
    }
});
</script>

<style scoped lang="scss">
.campaign-details-container {
    background: #f8f9fa;
    height: 100%;
}

.back-button-section {
    padding: 1rem 1.5rem 0.5rem;

    .back-btn {
        color: #6b7280;
        font-weight: 500;

        &:hover {
            color: #ff2c21;
        }
    }
}

.campaign-header {
    background: white;
    border-bottom: 1px solid #e5e7eb;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .header-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .header-left {
        display: flex;
        gap: 1.5rem;
        align-items: center;
    }

    .campaign-icon {
        width: 64px;
        height: 64px;
        background: linear-gradient(135deg, #ff2c21 0%, #ff5447 100%);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 2rem;
        box-shadow: 0 4px 12px rgba(255, 44, 33, 0.2);
    }

    .campaign-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: #111827;
        margin: 0 0 0.5rem 0;
    }

    .campaign-meta {
        display: flex;
        gap: 1rem;
        align-items: center;
        color: #6b7280;
        font-size: 0.875rem;

        .meta-item {
            display: flex;
            align-items: center;
            gap: 0.375rem;
        }
    }
}

.metrics-section {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    padding: 0 1.5rem;
    margin-bottom: 1.5rem;

    .metric-card {
        background: white;
        padding: 1.25rem;
        border-radius: 12px;
        display: flex;
        align-items: center;
        gap: 1rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        .metric-icon {
            width: 44px;
            height: 44px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.25rem;
        }

        .metric-content {
            .metric-label {
                font-size: 0.75rem;
                color: #6b7280;
                font-weight: 500;
            }

            .metric-value {
                font-size: 1.125rem;
                color: #111827;
                font-weight: 700;
            }
        }
    }
}

.two-column-layout {
    display: grid;
    grid-template-columns: 450px 1fr;
    gap: 1.5rem;
    padding: 0 1.5rem 1.5rem;
}

.section-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;

    .card-header {
        padding: 1.25rem;
        border-bottom: 1px solid #f3f4f6;
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .card-header-icon {
            width: 32px;
            height: 32px;
            background: #ff2c21;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }

        .card-header-title {
            font-size: 1rem;
            font-weight: 600;
            color: #111827;
            margin: 0;
        }
    }

    .card-body {
        padding: 1.25rem;
    }
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 600;

    .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
    }

    &.status-active {
        background: #d1fae5;
        color: #065f46;

        .status-dot {
            background: #059669;
        }
    }

    &.status-inactive {
        background: #f3f4f6;
        color: #374151;

        .status-dot {
            background: #6b7280;
        }
    }
}

.no-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    color: #9ca3af;

    .no-data-icon {
        font-size: 4rem;
        margin-bottom: 1rem;
    }

    .no-data-text {
        font-size: 1.25rem;
        font-weight: 500;
    }
}
</style>
